/**
 * Preview Proxy Adapter
 *
 * Custom axios adapter that routes API requests through the builder's
 * caching proxy via postMessage. When preview proxy mode is active, this
 * adapter replaces the default HTTP adapter on the CustomerApiClient.
 *
 * Flow:
 * 1. Hook calls getCustomerApiClient().someMethod(...)
 * 2. Axios request interceptors run (auth header is skipped in embedded mode)
 * 3. This adapter intercepts the outbound request
 * 4. Serializes method/url/data and sends PREVIEW_API_REQUEST to builder
 *    (params are serialized into the URL to preserve correct format)
 * 5. Builder checks cache / calls Customer Portal API / returns response
 * 6. handleProxyResponse() resolves the pending promise
 * 7. React Query processes the response as normal
 */
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { DUMMY_PORTAL_MESSAGE_EVENT_TYPE } from './events'
import { getParentOrigin } from './static'

const PROXY_TIMEOUT_MS = 30_000

interface PendingRequest {
  resolve: (response: AxiosResponse) => void
  reject: (error: Error) => void
  timer: ReturnType<typeof setTimeout>
}

const pendingRequests = new Map<string, PendingRequest>()

/**
 * Handle an incoming PREVIEW_API_RESPONSE message from the builder.
 * Resolves (or rejects) the pending promise for the matching requestId.
 */
export function handleProxyResponse(data: {
  requestId: string
  status: number
  statusText: string
  data: unknown
  headers: Record<string, string>
}): void {
  const pending = pendingRequests.get(data.requestId)

  if (!pending) return

  clearTimeout(pending.timer)
  pendingRequests.delete(data.requestId)

  const defaultResponse: AxiosResponse = {
    data: data.data,
    status: data.status,
    statusText: data.statusText,
    headers: data.headers || {},
    config: {
      headers: undefined
    }
  }

  if (data.status >= 400) {
    const error: Error & { response?: AxiosResponse; isAxiosError?: boolean } =
      new Error(`Request failed with status ${data.status}`)

    error.response = defaultResponse
    error.isAxiosError = true
    pending.reject(error)
  } else {
    pending.resolve(defaultResponse)
  }
}

/**
 * Custom axios adapter that proxies requests through the builder.
 * Installed on the CustomerApiClient when preview proxy mode is active.
 */
export function previewProxyAdapter(
  config: AxiosRequestConfig
): Promise<AxiosResponse> {
  return new Promise((resolve, reject) => {
    const requestId = uuidv4()

    const baseURL = config.baseURL || ''
    const rawUrl = config.url?.startsWith('http')
      ? config.url
      : `${baseURL}${config.url || ''}`

    // Serialize params into the URL to preserve the portal's serialization format.
    // Without this, params are sent separately via postMessage and re-serialized
    // by the builder's axios, which incorrectly stringifies nested objects/arrays
    // (e.g. context_entities becomes JSON strings instead of properly nested params).
    const url = config.params
      ? axios.getUri({
          url: rawUrl,
          params: config.params,
          paramsSerializer: config.paramsSerializer
        })
      : rawUrl

    const timer = setTimeout(() => {
      pendingRequests.delete(requestId)
      reject(
        new Error(
          `[Preview Proxy] Timeout after ${PROXY_TIMEOUT_MS}ms for ${config.method} ${url}`
        )
      )
    }, PROXY_TIMEOUT_MS)

    pendingRequests.set(requestId, { resolve, reject, timer })

    window.parent.postMessage(
      {
        type: DUMMY_PORTAL_MESSAGE_EVENT_TYPE.PREVIEW_API_REQUEST,
        requestId,
        method: (config.method || 'GET').toUpperCase(),
        url,
        data: config.data,
        headers: {
          'Accept-Language': config.headers?.['Accept-Language'] || 'en'
        }
      },
      getParentOrigin()
    )
  })
}

/**
 * Reject and clean up all pending proxy requests.
 * Called when exiting preview proxy mode.
 */
export function cleanupPendingRequests(): void {
  for (const [, pending] of pendingRequests) {
    clearTimeout(pending.timer)
    pending.reject(new Error('Preview proxy mode deactivated'))
  }
  pendingRequests.clear()
}
